import { get } from 'lodash';
import { SUBJECTS_SLUGS_TYPE } from 'platform-components/src/typings';
import { createSelector } from 'reselect';

import { UMKSubjectClassHintData } from 'common.components/typings';
import { getUMKHintData } from 'common.components/umk/selectors';
import { getCurrentClass, getIsInOwnClass } from 'common.components/user/selectors';
import { getSubjects } from 'store/courses/selectors';
import { getClassIdFromProps, getSubjectFromProps, intlSelector } from 'utils/selectors/selectors';

import { ReduxTeacherStore } from '../../typings';

import {
    MS_IN_DAY,
    // interfaces
    iSchoolbook,
    iSourceSchoolbook,
    iUMK,
    UMKModalStoreState,
} from './typings';
import { createUMKList, filterSchoolbooks, getUMKBySchoolbookId, mapSchoolbooksToSubject } from './utils';

const UMKModalStateSelector = (storeState: ReduxTeacherStore) => storeState.UMKModal;

/**
 * returns list of all umk from bunker
 */
export const getUMKList = (): Array<iUMK<iSchoolbook>> => {
    // @ts-ignore
    const sourceUMKData: Array<iUMK<iSourceSchoolbook>> = !isSSR ? window._data.umk.umkList : [];

    return createUMKList(sourceUMKData);
};

export const getQuery = createSelector(
    [UMKModalStateSelector, getSubjectFromProps],
    (state: UMKModalStoreState, subject: SUBJECTS_SLUGS_TYPE): string | null => get(state, [subject, 'query'])
);

/**
 * returns list of all umks, grouped by subject
 * @param subject
 * @param classNumber
 */
export const getSchoolbooksBySubject = createSelector(
    [getCurrentClass, getSubjectFromProps],
    (currentClass, subject: SUBJECTS_SLUGS_TYPE | null): Array<iSchoolbook> => {
        const classNumber = currentClass?.level;
        const umkList = getUMKList();
        const schoolbooksBySubject = mapSchoolbooksToSubject(umkList);

        if (!schoolbooksBySubject[subject]) {
            return [];
        }

        return schoolbooksBySubject[subject].filter((schoolbook) => {
            return !schoolbook.grades || (classNumber && schoolbook.grades.includes(classNumber));
        });
    }
);

export const getFilteredSchoolbooksBySubject = createSelector(
    [getSchoolbooksBySubject, getQuery],
    (schoolbooks, query): Array<iSchoolbook> => {
        return filterSchoolbooks(schoolbooks, query);
    }
);

/**
 * returns current list of schoolbooks
 * @param {Object} storeState
 * @return {Array}
 */
export const getSchoolbooks = createSelector(
    [UMKModalStateSelector, getSubjectFromProps],
    (state: UMKModalStoreState, subject: SUBJECTS_SLUGS_TYPE) => get(state, [subject, 'schoolbooksList'])
);

/**
 * gets selected schoolbook id
 * @param {Object} storeState
 * @return {String|null}
 */
export const getSelectedSchoolbookId = createSelector(
    [UMKModalStateSelector, getSubjectFromProps],
    (state: UMKModalStoreState, subject: SUBJECTS_SLUGS_TYPE): string | null =>
        get(state, [subject, 'selectedSchoolbookId'])
);

export const getSelectedSchoolbook = createSelector(
    [getSelectedSchoolbookId, getFilteredSchoolbooksBySubject],
    (selectedSchoolbookId, schoolbooksList) => {
        if (schoolbooksList) {
            return getUMKBySchoolbookId(schoolbooksList, selectedSchoolbookId);
        }

        return '';
    }
);

export const getSelectedSchoolbooks = (storeState: any) => {
    const UMKData = UMKModalStateSelector(storeState);
    const subjectsWithUMK = getSubjectsWithUMK(storeState);

    const selectedSchoolbooks: Record<SUBJECTS_SLUGS_TYPE, string> = {};

    Object.keys(UMKData).forEach((subject) => {
        if (subjectsWithUMK.includes(subject)) {
            selectedSchoolbooks[subject] = getSelectedSchoolbook(storeState, { subject });
        }
    });

    return selectedSchoolbooks;
};

export const getSubjectsWithUMK = createSelector([getUMKList], (umkList): Array<SUBJECTS_SLUGS_TYPE> => {
    const schoolbooksBySubject = mapSchoolbooksToSubject(umkList);

    return Object.keys(schoolbooksBySubject);
});

/**
 * returns true, if should show modal: has classId and subject, but not shown before
 */
export const getShouldShowUMKModal = createSelector(
    [getUMKHintData, getIsInOwnClass, getSubjects, getSchoolbooksBySubject, getClassIdFromProps, intlSelector],
    (
        umkHint,
        isInOwnTeam,
        subjects,
        schoolbooksBySubject,
        teamId,
        intl
        // eslint-disable-next-line max-params
    ) => {
        const isUMKModalOn = intl.formatMessage({ id: 'umk.modal.active' }) === 'true';
        const hasSubjects = subjects.length !== 0;

        const subjectUMKExists = schoolbooksBySubject.length !== 0;

        const { schoolbooks, lastSeen = '0' }: UMKSubjectClassHintData = get(
            umkHint,
            teamId || -1,
            {} as UMKSubjectClassHintData
        );
        const lastSeenTime: number = new Date(lastSeen).getTime();
        const alreadyGotData = Boolean(schoolbooks);
        const now = new Date();
        const SHOW_FREQUENCY_IN_DAYS = 2;
        const hasSeenToday: boolean = now.getTime() - lastSeenTime < SHOW_FREQUENCY_IN_DAYS * MS_IN_DAY;

        return isUMKModalOn && !alreadyGotData && !hasSeenToday && isInOwnTeam && hasSubjects && subjectUMKExists;
    }
);
